import { BLOOM_UNIVERSAL_CONTENT_TYPES } from '@/core/constants/contentfulTags';
import { logger } from '@/core/logger';
import {
  getSdkClient,
  mapAppLocaleToContentful,
} from '@/core/services/contentfulClient';
import {
  type BloomUniversalAssetFields,
  type BloomUniversalHubContent,
  type BloomUniversalHubFields,
  type BloomUniversalSpaceCardFields,
  type BloomUniversalSpaceContent,
  type BloomUniversalSpaceFields,
} from '../types/bloomUniversalContent';
import {
  findSpaceInHub,
  mapBloomUniversalHubFields,
  orderPageCardFields,
} from './mapBloomUniversalContent';
 
type AppLocale = 'en' | 'ar';
 
type ContentfulAssetLike = {
  readonly fields?: { readonly file?: unknown };
  readonly sys?: { readonly id?: string };
};
 
function extractAssetUrl(asset: unknown): string | undefined {
  if (!asset || typeof asset !== 'object') {
    return undefined;
  }
  const fields = (asset as { fields?: { file?: { url?: string } } }).fields;
  const url = fields?.file?.url;
  if (typeof url !== 'string' || !url.trim()) {
    return undefined;
  }
  return url.startsWith('//') ? `https:${url}` : url;
}
 
function resolveAsset(
  value: unknown,
  assetMap: Map<string, unknown>,
): ContentfulAssetLike | null {
  if (!value) {
    return null;
  }
  if (
    typeof value === 'object' &&
    (value as ContentfulAssetLike).fields?.file
  ) {
    return value as ContentfulAssetLike;
  }
  const id = (value as ContentfulAssetLike)?.sys?.id;
  if (id && assetMap.has(id)) {
    return (assetMap.get(id) as ContentfulAssetLike | undefined) ?? null;
  }
  return null;
}
 
function toAssetFields(
  value: unknown,
  assetMap: Map<string, unknown>,
): BloomUniversalAssetFields | undefined {
  const asset = resolveAsset(value, assetMap);
  const url = extractAssetUrl(asset);
  return url ? { url } : undefined;
}
 
function getString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}
 
function buildAssetMap(includes: { Asset?: unknown[] } | undefined): Map<string, unknown> {
  const assetMap = new Map<string, unknown>();
  (includes?.Asset ?? []).forEach((asset) => {
    const id = (asset as { sys?: { id?: string } })?.sys?.id;
    if (id) {
      assetMap.set(id, asset);
    }
  });
  return assetMap;
}
 
function buildEntryMap(includes: { Entry?: unknown[] } | undefined): Map<string, unknown> {
  const entryMap = new Map<string, unknown>();
  (includes?.Entry ?? []).forEach((entry) => {
    const id = (entry as { sys?: { id?: string } })?.sys?.id;
    if (id) {
      entryMap.set(id, entry);
    }
  });
  return entryMap;
}
 
/**
* The shared Contentful client returns raw REST payloads, so linked entries
* arrive as `{ sys: { type: 'Link' } }` without `fields`. Resolve them from
* `includes.Entry` before mapping.
*/
function resolveEntry(value: unknown, entryMap: Map<string, unknown>): unknown {
  if (!value || typeof value !== 'object') {
    return value;
  }
  if ((value as { fields?: unknown }).fields) {
    return value;
  }
  const id = (value as { sys?: { id?: string } }).sys?.id;
  return id ? entryMap.get(id) ?? value : value;
}
 
function mapSpaceCardEntry(
  entry: unknown,
  assetMap: Map<string, unknown>,
): (BloomUniversalSpaceCardFields & { sortOrder: number }) | null {
  if (!entry || typeof entry !== 'object') {
    return null;
  }
  const fields = (entry as { fields?: Record<string, unknown> }).fields ?? {};
  const cardKey = getString(fields.cardKey).trim();
  const title = getString(fields.title).trim();
  if (!cardKey || !title) {
    return null;
  }
  if (fields.isActive === false) {
    return null;
  }
  const subtitle = getString(fields.subtitle).trim() || undefined;
  const image = toAssetFields(fields.image, assetMap);
  const cardHref = getString(fields.cardHref).trim() || undefined;
  const sortOrder =
    typeof fields.sortOrder === 'number' && Number.isFinite(fields.sortOrder)
      ? fields.sortOrder
      : 0;
  return {
    cardKey,
    title,
    sortOrder,
    ...(subtitle ? { subtitle } : {}),
    ...(image ? { image } : {}),
    ...(cardHref ? { cardHref } : {}),
  };
}
 
function mapRawSpaceCard(
  rawInput: unknown,
  assetMap: Map<string, unknown>,
  entryMap: Map<string, unknown>,
): (BloomUniversalSpaceCardFields & { sortOrder: number }) | null {
  const raw = resolveEntry(rawInput, entryMap);
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  // Linked entry (resolved from includes) or already-embedded entry
  if ((raw as { fields?: unknown }).fields) {
    return mapSpaceCardEntry(raw, assetMap);
  }
  // Inline JSON object from Contentful Object field
  const obj = raw as Record<string, unknown>;
  const cardKey = getString(obj.cardKey).trim();
  const title = getString(obj.title).trim();
  if (!cardKey || !title) {
    return null;
  }
  if (obj.isActive === false) {
    return null;
  }
  const subtitle = getString(obj.subtitle).trim() || undefined;
  const sortOrder =
    typeof obj.sortOrder === 'number' && Number.isFinite(obj.sortOrder)
      ? obj.sortOrder
      : 0;
  const cardHref = getString(obj.cardHref).trim() || undefined;
  let image: BloomUniversalAssetFields | undefined;
  if (obj.image && typeof obj.image === 'object' && 'url' in (obj.image as object)) {
    image = { url: getString((obj.image as { url?: string }).url) };
  } else {
    image = toAssetFields(obj.image, assetMap);
  }
  return {
    cardKey,
    title,
    sortOrder,
    ...(subtitle ? { subtitle } : {}),
    ...(image?.url ? { image } : {}),
    ...(cardHref ? { cardHref } : {}),
  };
}
 
function mapSpaceEntryFields(
  fields: Record<string, unknown>,
  assetMap: Map<string, unknown>,
  entryMap: Map<string, unknown>,
): BloomUniversalSpaceFields | null {
  const spaceKey = getString(fields.spaceKey).trim();
  const title = getString(fields.title).trim();
  if (!spaceKey || !title) {
    return null;
  }
  if (fields.isActive === false) {
    return null;
  }

  const pageCardsRaw = Array.isArray(fields.pageCards) ? fields.pageCards : [];
  const pageCardsMapped = pageCardsRaw
    .map((card) => mapRawSpaceCard(card, assetMap, entryMap))
    .filter((card): card is BloomUniversalSpaceCardFields & { sortOrder: number } =>
      card !== null,
    )
    .map(({ sortOrder: _sortOrder, ...card }) => ({
      ...card,
      sortOrder: _sortOrder,
    }));
  const pageCards = orderPageCardFields(pageCardsMapped).map(
    ({ sortOrder: _sortOrder, ...card }) => card,
  );
 
  const tileImage = toAssetFields(fields.tileImage, assetMap);
  const icon = toAssetFields(fields.icon, assetMap);
  const pageHeroImage = toAssetFields(fields.pageHeroImage, assetMap);
  const pageHeadline = getString(fields.pageHeadline).trim() || undefined;
  const pageBody = getString(fields.pageBody).trim() || undefined;
  const pageCtaLabel = getString(fields.pageCtaLabel).trim() || undefined;
  const pageCtaHref = getString(fields.pageCtaHref).trim() || undefined;
  const cardsSectionTitle =
    getString(fields.cardsSectionTitle).trim() || undefined;

  return {
    spaceKey,
    title,
    gridSpan: getString(fields.gridSpan) as BloomUniversalSpaceFields['gridSpan'],
    sortOrder: typeof fields.sortOrder === 'number' ? fields.sortOrder : 0,
    isActive: true,
    pageCards,
    ...(cardsSectionTitle ? { cardsSectionTitle } : {}),
    ...(tileImage ? { tileImage } : {}),
    ...(icon ? { icon } : {}),
    ...(pageHeroImage ? { pageHeroImage } : {}),
    ...(pageHeadline ? { pageHeadline } : {}),
    ...(pageBody ? { pageBody } : {}),
    ...(pageCtaLabel ? { pageCtaLabel } : {}),
    ...(pageCtaHref ? { pageCtaHref } : {}),
  };
}
 
function mapHubEntryFields(
  fields: Record<string, unknown>,
  assetMap: Map<string, unknown>,
  entryMap: Map<string, unknown>,
): BloomUniversalHubFields | null {
  const pageKey = getString(fields.pageKey).trim();
  const spacesRaw = Array.isArray(fields.spaces) ? fields.spaces : [];
  const spaces = spacesRaw
    .map((entry) => {
      const entryFields =
        (resolveEntry(entry, entryMap) as { fields?: Record<string, unknown> })
          ?.fields ?? null;
      if (!entryFields) {
        return null;
      }
      return mapSpaceEntryFields(entryFields, assetMap, entryMap);
    })
    .filter((space): space is BloomUniversalSpaceFields => space !== null);
 
  const heroBackground = toAssetFields(fields.heroBackground, assetMap);
  const ctaHref = getString(fields.ctaHref).trim() || undefined;
  const ctaSpaceKey = getString(fields.ctaSpaceKey).trim() || undefined;
  // QA CMS uses `contactPhone`; docs/fallback use `phoneNumber`.
  const phoneNumber =
    getString(fields.contactPhone).trim() ||
    getString(fields.phoneNumber).trim() ||
    undefined;
  const phoneIcon = toAssetFields(fields.phoneIcon, assetMap);
  const arrowIcon = toAssetFields(fields.arrowIcon, assetMap);
  const breadcrumbChevronIcon = toAssetFields(
    fields.breadcrumbChevronIcon,
    assetMap,
  );

  // Contentful hub field IDs: exploreTitle, exploreSubtitle (mosaic section above tiles).
  const exploreTitle = getString(fields.exploreTitle).trim() || undefined;
  const exploreSubtitle = getString(fields.exploreSubtitle).trim() || undefined;
 
  return {
    experienceEyebrow: getString(fields.experienceEyebrow),
    headline: getString(fields.headline),
    subheadline: getString(fields.subheadline),
    ctaLabel: getString(fields.ctaLabel),
    spaces,
    ...(pageKey ? { pageKey } : {}),
    ...(ctaHref ? { ctaHref } : {}),
    ...(ctaSpaceKey ? { ctaSpaceKey } : {}),
    ...(heroBackground ? { heroBackground } : {}),
    ...(phoneNumber ? { phoneNumber } : {}),
    ...(phoneIcon ? { phoneIcon } : {}),
    ...(arrowIcon ? { arrowIcon } : {}),
    ...(breadcrumbChevronIcon ? { breadcrumbChevronIcon } : {}),
    ...(exploreTitle ? { exploreTitle } : {}),
    ...(exploreSubtitle ? { exploreSubtitle } : {}),
  };
}
 
/**
 * Fetch the published Bloom Universal hub (`content_type=bloomUniversalHub`).
 * CDA: content_type=bloomUniversalHub&include=2&limit=1
 * Throws on Contentful client / network failure (caller may show fallback).
 * Returns null when the entry is missing (successful empty response).
 */
export async function fetchBloomUniversalHub(
  locale: AppLocale,
): Promise<BloomUniversalHubContent | null> {
  try {
    const client = getSdkClient();
    const cfLocale = mapAppLocaleToContentful(locale);
    const res = await client.getEntries({
      content_type: BLOOM_UNIVERSAL_CONTENT_TYPES.hub,
      locale: cfLocale,
      include: 2,
      limit: 1,
    });

    const item = res.items?.[0];
    if (!item?.fields) {
      logger.warn('BloomUniversal', 'Hub entry not found', { locale: cfLocale });
      return null;
    }

    const assetMap = buildAssetMap(res.includes);
    const entryMap = buildEntryMap(res.includes);
    const raw = mapHubEntryFields(
      item.fields as Record<string, unknown>,
      assetMap,
      entryMap,
    );
    if (!raw) {
      return null;
    }
    const hub = mapBloomUniversalHubFields(raw);
    if (hub.spaces.length === 0) {
      logger.warn('BloomUniversal', 'Hub resolved with no spaces', {
        locale: cfLocale,
        linkedSpaces: Array.isArray(item.fields.spaces)
          ? item.fields.spaces.length
          : 0,
        includedEntries: entryMap.size,
      });
    }
    return hub;
  } catch (error) {
    logger.error('BloomUniversal', 'fetchBloomUniversalHub failed', { error, locale });
    throw error;
  }
}

/**
 * Resolve one space from the hub response (spaces are nested includes).
 * Throws when hub fetch fails; returns null when the space is missing/inactive.
 */
export async function fetchBloomUniversalSpace(
  spaceKey: string,
  locale: AppLocale,
): Promise<BloomUniversalSpaceContent | null> {
  const key = spaceKey.trim();
  if (!key) {
    return null;
  }

  const hub = await fetchBloomUniversalHub(locale);
  if (!hub) {
    return null;
  }

  const space = findSpaceInHub(hub, key);
  if (!space || space.isActive === false) {
    logger.warn('BloomUniversal', 'Space not found on hub', {
      spaceKey: key,
      locale,
    });
    return null;
  }
  return space;
}
 
 